import { DashboardDateRangeControls } from '@/features/dashboard/components/DashboardDateRangeControls';
import { MetricValueCardsRow } from '@/features/metrics/components/MetricValueCardsRow';
import { StatusMetricCardsRow } from '@/features/metrics/components/StatusMetricCardsRow';
import { r, s } from '@/theme/tokens';
import { uiTokens } from '@/theme/uiTokens';
import { Inline, Stack } from '@/ui';
import {
  BarChart,
  Box,
  ChevronRightIcon,
  formatStatusLabel,
  getStatusChipColor,
  IconButton,
  LineChart,
  StackedBarChart,
  Text,
} from '@/ui/primitives';
import { Tab, Tabs } from '@/ui/primitives/Tabs';
import { chartBarSizing } from '@/ui/theme';
import { landingCopy } from './landingCopy';
import type { DashboardRange } from '@/features/dashboard/hooks';
import type { MetricValueItem } from '@/features/metrics/components/metricValueItems';
import type { MetricCardItem } from '@/features/metrics/components/statusMetricCards';
import type { ReactElement } from 'react';

type ChartTab = 'status' | 'pass-rate' | 'frequency' | 'duration';

type StatusTrendChartRow = {
  label: string;
  passed: number;
  failed: number;
  skipped: number;
};

type SeriesRow = {
  label: string;
  passRate?: number;
  frequency?: number;
  duration?: number;
};

type QualityHighlightsCarouselSectionProps = {
  width: number;
  chartHeight: number;
  toggleButtonHeight: number;
  rangeTab: DashboardRange;
  onRangeTabChange: (next: DashboardRange) => void;
  statusCards: MetricCardItem[];
  metricCardsForRange: MetricValueItem[];
  chartTab: ChartTab;
  onChartTabChange: (next: ChartTab) => void;
  statusTrendChartData: StatusTrendChartRow[];
  passRateChartData: SeriesRow[];
  frequencyChartData: SeriesRow[];
  durationChartData: SeriesRow[];
  carouselIndex: number;
  direction: 'next' | 'prev';
  onPrev: () => void;
  onNext: () => void;
  onSelect: (index: number) => void;
  slides: Array<{ id: string }>;
};

export const QualityHighlightsCarouselSection = ({
  width,
  chartHeight,
  toggleButtonHeight,
  rangeTab,
  onRangeTabChange,
  statusCards,
  metricCardsForRange,
  chartTab,
  onChartTabChange,
  statusTrendChartData,
  passRateChartData,
  frequencyChartData,
  durationChartData,
  carouselIndex,
  direction,
  onPrev,
  onNext,
  onSelect,
  slides,
}: QualityHighlightsCarouselSectionProps): ReactElement => {
  return (
    <Box component="section" id="example-results">
      <Box sx={{ maxWidth: width, mx: 'auto', width: '100%' }}>
        <Box sx={{ mb: s('md'), textAlign: 'center' }}>
          <Text role="sectionTitle" component="h2" transform="none" mb="md">
            {landingCopy.qualityHighlights.heading}
          </Text>
          <Text role="bodyMuted">{landingCopy.qualityHighlights.intro}</Text>
        </Box>
        <Box
          sx={{
            border: 'none',
            backgroundColor: 'transparent',
            borderRadius: r('none'),
            overflow: 'hidden',
            width: '100%',
            minWidth: 0,
            contain: 'layout paint',
            isolation: 'isolate',
          }}
        >
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: `auto minmax(0, 1fr) auto` },
              alignItems: 'center',
              gap: s('sm'),
              width: '100%',
              minWidth: 0,
            }}
          >
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                justifyContent: 'center',
                minWidth: s('3xl'),
              }}
            >
              <IconButton
                aria-label="Previous slide"
                size="large"
                onClick={onPrev}
                sx={(theme) => ({
                  backgroundColor: 'transparent',
                  border: 'none',
                  '&:hover': { backgroundColor: 'transparent' },
                  '& .MuiBox-root': {
                    width: theme.app.iconSizes.xl,
                    height: theme.app.iconSizes.xl,
                  },
                  '& svg': {
                    width: '100%',
                    height: '100%',
                  },
                })}
              >
                <Box sx={{ transform: 'rotate(180deg)' }}>
                  <ChevronRightIcon />
                </Box>
              </IconButton>
            </Box>
            <Box sx={{ width: '100%', minWidth: 0 }}>
              <Box
                key={`carousel-primary-${carouselIndex}`}
                sx={(theme) => ({
                  px: s('lg'),
                  py: s('lg'),
                  display: 'grid',
                  gap: s('sm'),
                  minWidth: 0,
                  width: '100%',
                  maxWidth: '100%',
                  mx: 'auto',
                  minHeight: '264px',
                  alignContent: 'start',
                  animation: `landing-slide-${
                    direction === 'next' ? 'left' : 'right'
                  } 420ms ${theme.app.motion.easing.standard}`,
                  '@keyframes landing-slide-left': {
                    from: { opacity: 0, transform: `translateX(${s('lg')})` },
                    to: { opacity: 1, transform: 'translateX(0)' },
                  },
                  '@keyframes landing-slide-right': {
                    from: { opacity: 0, transform: `translateX(-${s('lg')})` },
                    to: { opacity: 1, transform: 'translateX(0)' },
                  },
                  '& .MuiDataGrid-root': {
                    width: '100%',
                    minWidth: 0,
                    maxWidth: '100%',
                  },
                })}
              >
                {carouselIndex === 0 ? (
                  <Stack gap="lg" sx={{ pt: s('lg') }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <DashboardDateRangeControls
                        value={rangeTab}
                        onChange={onRangeTabChange}
                        includeLatest
                        fullWidthOnMobile
                        showQuickFiltersLabel={false}
                        buttonSx={{
                          minHeight: toggleButtonHeight,
                          height: toggleButtonHeight,
                          paddingY: s('xxs'),
                          paddingX: s('md'),
                          lineHeight: 1,
                        }}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <StatusMetricCardsRow cards={statusCards} centerWrapped />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <MetricValueCardsRow items={metricCardsForRange} centerWrapped />
                    </Box>
                  </Stack>
                ) : carouselIndex === 1 ? (
                  <Stack gap="md">
                    <Tabs
                      value={chartTab}
                      onChange={(_, next) => {
                        onChartTabChange(next as ChartTab);
                      }}
                      aria-label="Landing chart tabs"
                    >
                      <Tab label="Status" value="status" />
                      <Tab label="Pass Rate" value="pass-rate" />
                      <Tab label="Frequency" value="frequency" />
                      <Tab label="Duration" value="duration" />
                    </Tabs>
                    {chartTab === 'status' ? (
                      <StackedBarChart
                        data={statusTrendChartData}
                        series={[
                          {
                            key: 'passed',
                            label: formatStatusLabel('passed'),
                            colorKey: getStatusChipColor('passed'),
                          },
                          {
                            key: 'failed',
                            label: formatStatusLabel('failed'),
                            colorKey: getStatusChipColor('failed'),
                          },
                          {
                            key: 'skipped',
                            label: formatStatusLabel('skipped'),
                            colorKey: getStatusChipColor('skipped'),
                          },
                        ]}
                        height={chartHeight}
                        skipAnimation
                        disableInteraction
                        disableTooltip
                      />
                    ) : chartTab === 'pass-rate' ? (
                      <BarChart
                        data={passRateChartData}
                        series={[
                          { key: 'passRate', label: '', color: uiTokens.statusColors.green },
                        ]}
                        height={chartHeight}
                        showLegend={false}
                        yAxisMin={0}
                        yAxisMax={100}
                        categoryGapRatio={chartBarSizing.landing.categoryGapRatio}
                        barGapRatio={chartBarSizing.landing.barGapRatio}
                      />
                    ) : chartTab === 'frequency' ? (
                      <LineChart
                        data={frequencyChartData}
                        series={[{ key: 'frequency', label: '', color: 'var(--color-pink)' }]}
                        height={chartHeight}
                        marginLeft={-4}
                        yAxisWidth={56}
                        showLegend={false}
                        disableInteraction
                      />
                    ) : (
                      <LineChart
                        data={durationChartData}
                        series={[{ key: 'duration', label: '' }]}
                        height={chartHeight}
                        marginLeft={-4}
                        yAxisWidth={56}
                        showLegend={false}
                        disableInteraction
                      />
                    )}
                  </Stack>
                ) : null}
              </Box>
            </Box>
            <Box
              sx={{
                display: { xs: 'none', sm: 'flex' },
                justifyContent: 'center',
                minWidth: s('3xl'),
              }}
            >
              <IconButton
                aria-label="Next slide"
                size="large"
                onClick={onNext}
                sx={(theme) => ({
                  backgroundColor: 'transparent',
                  border: 'none',
                  '&:hover': { backgroundColor: 'transparent' },
                  '& .MuiBox-root': {
                    width: theme.app.iconSizes.xl,
                    height: theme.app.iconSizes.xl,
                  },
                  '& svg': {
                    width: '100%',
                    height: '100%',
                  },
                })}
              >
                <Box>
                  <ChevronRightIcon />
                </Box>
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box
        sx={{
          mt: s('md'),
          display: { xs: 'none', sm: 'flex' },
          justifyContent: 'center',
          width: '100%',
          maxWidth: width,
          mx: 'auto',
        }}
      >
        <Inline gap="md" align="center" sx={{ width: '100%', justifyContent: 'center' }}>
          {slides.map((slide, index) => (
            <Box
              key={slide.id}
              component="button"
              type="button"
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => {
                onSelect(index);
              }}
              sx={(theme) => ({
                width: s('md'),
                height: s('md'),
                borderRadius: r('md'),
                border: 'none',
                padding: s('none'),
                cursor: 'pointer',
                backgroundColor:
                  index === carouselIndex ? theme.app.colors.text : theme.app.colors.divider,
              })}
            />
          ))}
        </Inline>
      </Box>
      <Box
        sx={{
          mt: s('lg'),
          display: { xs: 'flex', sm: 'none' },
          justifyContent: 'center',
          width: '100%',
        }}
      >
        <Inline gap="md" align="center" justify="center" wrap="nowrap" sx={{ width: '100%' }}>
          <IconButton
            aria-label="Previous slide"
            size="large"
            onClick={onPrev}
            sx={(theme) => ({
              backgroundColor: 'transparent',
              border: 'none',
              '&:hover': { backgroundColor: 'transparent' },
              '& .MuiBox-root': {
                width: theme.app.iconSizes.xl,
                height: theme.app.iconSizes.xl,
              },
              '& svg': {
                width: '100%',
                height: '100%',
              },
            })}
          >
            <Box sx={{ transform: 'rotate(180deg)' }}>
              <ChevronRightIcon />
            </Box>
          </IconButton>
          <Inline gap="md" align="center" wrap="nowrap" sx={{ justifyContent: 'center' }}>
            {slides.map((slide, index) => (
              <Box
                key={`${slide.id}-mobile`}
                component="button"
                type="button"
                aria-label={`Go to slide ${index + 1}`}
                onClick={() => {
                  onSelect(index);
                }}
                sx={(theme) => ({
                  width: s('md'),
                  height: s('md'),
                  borderRadius: r('md'),
                  border: 'none',
                  padding: s('none'),
                  cursor: 'pointer',
                  backgroundColor:
                    index === carouselIndex ? theme.app.colors.text : theme.app.colors.divider,
                })}
              />
            ))}
          </Inline>
          <IconButton
            aria-label="Next slide"
            size="large"
            onClick={onNext}
            sx={(theme) => ({
              backgroundColor: 'transparent',
              border: 'none',
              '&:hover': { backgroundColor: 'transparent' },
              '& .MuiBox-root': {
                width: theme.app.iconSizes.xl,
                height: theme.app.iconSizes.xl,
              },
              '& svg': {
                width: '100%',
                height: '100%',
              },
            })}
          >
            <Box>
              <ChevronRightIcon />
            </Box>
          </IconButton>
        </Inline>
      </Box>
    </Box>
  );
};
